import PropTypes from "prop-types";
import { Button, Empty, Select, Spin } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import CustomPagination from "../../../components/custom-component/CustomPagination";
import { MobileFilterBar } from "../../../components/data-table";
import {
    RESERVATION_FILTER_KEYS as KEYS,
    RESERVATION_LIST_ACTION_KEYS as ACTIONS,
    RESERVATION_TAB_KEYS as TABS,
    buildReservationActionItems,
    endReservation,
    getAssignmentOptions,
    getReservationFilterChipProps,
    getReservationStatusBadge,
    getTabSectionTitleKey,
    startReservation,
    viewReservation,
} from "../list";
import ReservationCard from "./ReservationCard";

function ReservationListPanel({ list }) {
    const { t } = useTranslation("reservations");
    const tableT = useTranslation("table").t;
    const history = useHistory();
    const { tabKey, table, assignment, filteredRows, pageRows, loading } = list;

    const handleAction = (key, reservation) => {
        switch (key) {
            case ACTIONS.VIEW:
                viewReservation(history, reservation);
                break;
            case ACTIONS.START_RESERVATION:
                startReservation(history, reservation);
                break;
            case ACTIONS.END_RESERVATION:
                endReservation(history, reservation);
                break;
            default:
                break;
        }
    };

    const renderEmpty = () => {
        if (table.hasActiveFilters) {
            return (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={tableT("noResults")}>
                    <Button icon={<ReloadOutlined />} onClick={table.resetFilters}>
                        {tableT("resetFilters")}
                    </Button>
                </Empty>
            );
        }
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("noReservations")} />;
    };

    return (
        <div className="flex flex-col gap-4">
            <h2 className="flex items-center gap-2 text-lg font-bold text-[#2F2F46]">
                {t(getTabSectionTitleKey(tabKey))}
                <span className="rounded-full bg-[#EEEEFE] px-2 py-0.5 text-xs font-semibold tabular-nums text-[#5D5DF6]">
                    {filteredRows.length}
                </span>
            </h2>

            <MobileFilterBar
                filters={table}
                search={{ key: KEYS.SEARCH, placeholder: t("listSearchPlaceholder") }}
                {...getReservationFilterChipProps(t)}
                extra={
                    tabKey === TABS.UPCOMING ? (
                        <Select
                            size="large"
                            value={assignment}
                            onChange={(value) => table.setFilter(KEYS.ASSIGNMENT, value === "all" ? null : value)}
                            options={getAssignmentOptions(t)}
                            aria-label={t("carAssignment")}
                        />
                    ) : null
                }
            />

            {loading && pageRows.length === 0 ? (
                <div className="flex justify-center py-16">
                    <Spin size="large" />
                </div>
            ) : pageRows.length === 0 ? (
                renderEmpty()
            ) : (
                <div className="relative min-h-[120px]">
                    {loading ? (
                        <div
                            className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/70"
                            aria-busy="true"
                            aria-live="polite">
                            <Spin />
                        </div>
                    ) : null}
                    <div
                        className={`flex flex-col gap-3${loading ? " pointer-events-none select-none opacity-50" : ""}`}
                        aria-hidden={loading}>
                        {pageRows.map((reservation) => (
                            <ReservationCard
                                key={reservation.id}
                                reservation={reservation}
                                statusBadge={getReservationStatusBadge(reservation, tabKey, t)}
                                menuItems={buildReservationActionItems(reservation, tabKey, t)}
                                onMenuClick={({ key }) => handleAction(key, reservation)}
                                t={t}
                                showCarNeededWarning={tabKey === TABS.UPCOMING}
                                showOverdueWarning={
                                    tabKey === TABS.ONGOING && new Date(reservation.dropoffDate) < new Date()
                                }
                            />
                        ))}
                    </div>
                </div>
            )}

            {filteredRows.length > 0 ? (
                <div className="flex min-w-0 w-full justify-end">
                    <CustomPagination
                        variant="spaced"
                        totalItem={filteredRows.length}
                        pageSize={list.pageSize}
                        setPageSize={list.setPageSize}
                        currentPage={list.currentPage}
                        setCurrentPage={list.setCurrentPage}
                        pageSizeOptions={[10, 20, 50, 100]}
                        showSizeChanger
                    />
                </div>
            ) : null}

        </div>
    );
}

ReservationListPanel.propTypes = {
    list: PropTypes.shape({
        tabKey: PropTypes.string.isRequired,
        table: PropTypes.object.isRequired,
        assignment: PropTypes.string.isRequired,
        filteredRows: PropTypes.array.isRequired,
        pageRows: PropTypes.array.isRequired,
        loading: PropTypes.bool.isRequired,
        currentPage: PropTypes.number.isRequired,
        setCurrentPage: PropTypes.func.isRequired,
        pageSize: PropTypes.number.isRequired,
        setPageSize: PropTypes.func.isRequired,
    }).isRequired,
};

export default ReservationListPanel;
